import { useRef, useEffect, type MouseEvent as ReactMouseEvent } from 'react'

/**
 * Makes a fixed-position modal draggable by its header.
 *
 * Best-practice features:
 * - Viewport boundary clamping (modal can't be dragged off-screen)
 * - Drag threshold to prevent accidental drags from clicks
 * - requestAnimationFrame for smooth, jank-free movement
 * - Resets position when modal unmounts (fresh position on reopen)
 * - Cursor feedback: grab on hover, grabbing while dragging
 */
export function useDraggable<T extends HTMLElement>() {
    const modalRef = useRef<T>(null)
    const dragging = useRef(false)
    const dragStarted = useRef(false)
    const startX = useRef(0)
    const startY = useRef(0)
    const savedX = useRef(0)
    const savedY = useRef(0)
    const rafId = useRef(0)

    const DRAG_THRESHOLD = 3 // px — ignore micro-movements from clicks

    useEffect(() => {
        const clampToViewport = (dx: number, dy: number): [number, number] => {
            const el = modalRef.current
            if (!el) return [dx, dy]
            const rect = el.getBoundingClientRect()
            const vw = window.innerWidth
            const vh = window.innerHeight
            const minVisible = 40 // px — keep at least this much visible on each edge

            // Current center before offset
            const centerX = vw / 2
            const centerY = vh / 2

            // Clamp so that at least minVisible px of the modal stays in the viewport
            const minDx = -(centerX + rect.width / 2 - minVisible)
            const maxDx = centerX + rect.width / 2 - minVisible
            const minDy = -(centerY + rect.height / 2 - minVisible)
            const maxDy = centerY - minVisible // Don't let top edge go above viewport

            return [
                Math.max(minDx, Math.min(maxDx, dx)),
                Math.max(minDy, Math.min(maxDy, dy)),
            ]
        }

        const onMouseMove = (e: globalThis.MouseEvent) => {
            if (!dragging.current || !modalRef.current) return

            const rawDx = savedX.current + e.clientX - startX.current
            const rawDy = savedY.current + e.clientY - startY.current

            // Require minimum movement before starting actual drag
            if (!dragStarted.current) {
                const dist = Math.abs(e.clientX - startX.current) + Math.abs(e.clientY - startY.current)
                if (dist < DRAG_THRESHOLD) return
                dragStarted.current = true
                document.body.style.cursor = 'grabbing'
            }

            cancelAnimationFrame(rafId.current)
            rafId.current = requestAnimationFrame(() => {
                if (!modalRef.current) return
                const [dx, dy] = clampToViewport(rawDx, rawDy)
                modalRef.current.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`
            })
        }

        const onMouseUp = (e: globalThis.MouseEvent) => {
            if (!dragging.current) return
            cancelAnimationFrame(rafId.current)
            if (dragStarted.current) {
                savedX.current += e.clientX - startX.current
                savedY.current += e.clientY - startY.current
                // Re-clamp the saved position
                const [clampedX, clampedY] = clampToViewport(savedX.current, savedY.current)
                savedX.current = clampedX
                savedY.current = clampedY
            }
            dragging.current = false
            dragStarted.current = false
            document.body.style.userSelect = ''
            document.body.style.cursor = ''
        }

        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
        return () => {
            document.removeEventListener('mousemove', onMouseMove)
            document.removeEventListener('mouseup', onMouseUp)
            cancelAnimationFrame(rafId.current)
        }
    }, [])

    // Reset position when modal unmounts so reopening starts centered
    useEffect(() => {
        return () => {
            savedX.current = 0
            savedY.current = 0
        }
    }, [])

    const onHeaderMouseDown = (e: ReactMouseEvent) => {
        if ((e.target as HTMLElement).closest('button, input, select, textarea, a')) return
        e.preventDefault()
        dragging.current = true
        dragStarted.current = false
        startX.current = e.clientX
        startY.current = e.clientY
        document.body.style.userSelect = 'none'
    }

    return { modalRef, onHeaderMouseDown }
}
